import type { StateStorage } from 'zustand/middleware';
import { isSingleOriginHost } from '@/config/portals';

/**
 * Cross-subdomain session storage (Phase 4 — subdomain split).
 *
 * Keeps the full auth state in localStorage (unchanged behaviour) but also
 * mirrors a trimmed copy into a cookie scoped to the shared parent domain
 * (e.g. `.tutara-prototype.trigear.in`). A session started on the learner host
 * is then picked up on the admin subdomain on first paint — silent SSO with no
 * second login — and vice-versa. On localhost there is no parent domain, so it
 * degrades to plain localStorage.
 *
 * Security note: the cookie is JS-readable (not HttpOnly) because the app reads
 * the access token from the store — the same XSS exposure as the existing
 * localStorage persistence, now shared across sibling subdomains only. It is
 * set `Secure; SameSite=Lax` and carries no more than localStorage already did.
 */

const COOKIE_NAME = 'tutara_session';
const MAX_AGE_SECONDS = 60 * 60 * 24 * 7; // 7 days — matches Keycloak SSO max lifespan
// Encoded cookies above ~4KB are dropped by browsers; stay well under.
const MAX_ENCODED_LENGTH = 3800;

/** Parent domain to scope the session cookie to, or null when not applicable. */
function cookieDomain(): string | null {
  const override = import.meta.env.VITE_SESSION_COOKIE_DOMAIN as string | undefined;
  if (override) return override;
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (isSingleOriginHost(host)) return null; // localhost: no shared parent
  const parent = host.startsWith('admin.') ? host.slice('admin.'.length) : host;
  return '.' + parent;
}

/**
 * Drop bulky, re-fetchable fields so the session fits in a ~4KB cookie.
 * `organizations[]` is re-hydrated by useOrganizationSync right after auth, so
 * it need not survive the cross-subdomain hand-off.
 */
function trimForCookie(value: string): string {
  try {
    const parsed = JSON.parse(value);
    if (parsed && parsed.state) {
      parsed.state = { ...parsed.state, organizations: [] };
    }
    return JSON.stringify(parsed);
  } catch {
    return value;
  }
}

function writeCookie(value: string): void {
  const domain = cookieDomain();
  if (!domain) return;
  const encoded = encodeURIComponent(trimForCookie(value));
  if (encoded.length > MAX_ENCODED_LENGTH) {
    // Better to skip the hand-off than to write a truncated, corrupt session.
    console.warn('[auth] session too large for cross-subdomain cookie; skipping SSO hand-off');
    return;
  }
  document.cookie = `${COOKIE_NAME}=${encoded}; Domain=${domain}; Path=/; Max-Age=${MAX_AGE_SECONDS}; Secure; SameSite=Lax`;
}

function readCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  try {
    return decodeURIComponent(match.slice(COOKIE_NAME.length + 1));
  } catch {
    return null;
  }
}

function clearCookie(): void {
  const domain = cookieDomain();
  if (!domain) return;
  document.cookie = `${COOKIE_NAME}=; Domain=${domain}; Path=/; Max-Age=0; Secure; SameSite=Lax`;
}

export const crossSubdomainStorage: StateStorage = {
  getItem: (name) => {
    const local = localStorage.getItem(name);
    if (local) return local;
    // First visit on a sibling subdomain: bootstrap from the shared cookie.
    return readCookie();
  },
  setItem: (name, value) => {
    localStorage.setItem(name, value);
    writeCookie(value);
  },
  removeItem: (name) => {
    localStorage.removeItem(name);
    clearCookie();
  },
};
